import React, {useEffect} from "react";
import {observer} from "mobx-react-lite";
import {sportModules} from "../../models/Sports/registry";
import PageLayout from "../PageLayout";
import appStore from "../../storage/AppStore";
import AwardsList from "../Awards/AwardsList";
import "../../common/game.css";

const GameResult = ({game, role}) => {
  useEffect(() => {
    if (!game) return;
    game.ensureResultInitialized?.();
  }, [game]);

  if (!game) return null;

  const mod = sportModules[game.sport_id];
  const View = mod?.View;

  const teams = [
    ...(game.game_team_1 ? [{id: game.game_team_1.id, name: game.game_team_1.name}] : []),
    ...(game.game_team_2 ? [{id: game.game_team_2.id, name: game.game_team_2.name}] : [])
  ];

  const context = {
    company_id: game.company_id,
    tournament_id: game.tournament_id || null,
    game_id: game.id,
    team_class_id: null,
    guest_company_id: game.guest_company_id
  };

  const r = game.result;
  const firstName = game.company?.name || "-";
  const secondName = game.guest_company?.name || appStore.tr.side2;
  const winnerName = r?.winner === "side1" ? firstName : r?.winner === "side2" ? secondName : "-";

  return (
    <PageLayout
      header={
        <div className="flex align-items-center gap-3 w-full min-w-0">
          <i className="pi pi-flag text-primary text-xl flex-shrink-0" aria-hidden="true"/>
          <div className="min-w-0 flex-1">
            <h4 className="m-0 font-bold line-height-3">{game.name}</h4>
            <div className="flex align-items-center flex-wrap gap-3 mt-1 text-sm text-color-secondary">
              {game.start_time_display && (
                <span className="flex align-items-center gap-2">
                  <i className="pi pi-calendar" aria-hidden="true"/>
                  {game.start_time_display}
                </span>
              )}
              {game.address && (
                <span className="flex align-items-center gap-2">
                  <i className="pi pi-map-marker" aria-hidden="true"/>
                  {game.address}
                </span>
              )}
            </div>
          </div>
        </div>
      }
      filters={
        <div className="game-page p-3 md:p-4">
          {game.tournament_id > 0 && (
            <div className="flex align-items-center justify-content-center gap-2 mb-3">
              <span className="badge badge-tournament">
                <i className="pi pi-trophy" aria-hidden="true"/>
                {game.tournament.name}
              </span>
            </div>
          )}

          <div className="flex align-items-center justify-content-center gap-2 mb-3">
            <i className="pi pi-chart-bar text-primary" aria-hidden="true"/>
            <h4 className="m-0 font-bold">{appStore.tr.game_results}</h4>
          </div>

          <div className="game-results-centered surface-ground border-round-xl p-3 md:p-4">
            <div className="side">
              <div className="side-avatar">
                {game.company?.logo ? (
                  <img className="avatar-img" src={game.company.logo} alt={firstName}/>
                ) : (
                  <div className="avatar-fallback"/>
                )}
              </div>
              <div className="side-name" title={firstName}>{firstName}</div>
            </div>

            <div className="game-result" aria-label={appStore.tr.game_results}>
              <span className={`score ${r?.winner === "side1" ? "winner" : ""}`}>{r?.final?.side1 ?? 0}</span>
              <span className="score-sep">:</span>
              <span className={`score ${r?.winner === "side2" ? "winner" : ""}`}>{r?.final?.side2 ?? 0}</span>
            </div>

            <div className="side">
              <div className="side-avatar">
                {game.guest_company?.logo ? (
                  <img className="avatar-img" src={game.guest_company.logo} alt={secondName}/>
                ) : (
                  <div className="avatar-fallback"/>
                )}
              </div>
              <div className="side-name" title={secondName}>{secondName}</div>
            </div>
          </div>

          <div className="flex flex-wrap align-items-center justify-content-center gap-2 mt-3 mb-4">
            <span className="text-sm text-color-secondary">{appStore.tr.winner}:</span>
            <span className={`badge ${r?.winner === "side1" || r?.winner === "side2" ? "badge-success" : ""}`}>
              <i className="pi pi-trophy" aria-hidden="true"/>
              {winnerName}
            </span>
          </div>

          <div className="flex align-items-center gap-2 mb-2 pt-3 border-top-1 surface-border">
            <i className="pi pi-users text-primary" aria-hidden="true"/>
            <h4 className="m-0 font-bold">{appStore.tr.team_composition}</h4>
          </div>

          <div className="grid m-0">
            <div className="col-12 md:col-6 p-2">
              <div className="competition-detail-item h-full">
                <div className="flex align-items-center gap-2 mb-3">
                  <i className="pi pi-shield text-primary" aria-hidden="true"/>
                  <span className="font-semibold">{firstName}</span>
                  <span className="badge badge-upcoming ml-auto">{(game.team_members_1 || []).length}</span>
                </div>
                <div className="flex flex-column gap-2">
                  {(game.team_members_1 || []).map((p) => (
                    <div key={p.id} className="flex align-items-center gap-2 py-2 border-bottom-1 surface-border min-w-0">
                      <span className="badge flex-shrink-0">{p.forma_number || 0}</span>
                      <span className="min-w-0 text-sm font-medium" title={p.title}>{p.title}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="col-12 md:col-6 p-2">
              <div className="competition-detail-item h-full">
                <div className="flex align-items-center gap-2 mb-3">
                  <i className="pi pi-shield text-primary" aria-hidden="true"/>
                  <span className="font-semibold">{secondName}</span>
                  <span className="badge badge-upcoming ml-auto">{(game.team_members_2 || []).length}</span>
                </div>
                <div className="flex flex-column gap-2">
                  {(game.team_members_2 || []).map((p) => (
                    <div key={p.id} className="flex align-items-center gap-2 py-2 border-bottom-1 surface-border min-w-0">
                      {p.forma_number && <span className="badge flex-shrink-0">{p.forma_number || 0}</span>}
                      <span className="min-w-0 text-sm font-medium" title={p.title}>{p.title}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      }
    >
      <AwardsList
        role={role}
        scope="MATCH"
        awardStore={game.award}
        context={context}
        sport_id={game.sport_id}
        teams={teams}
      />
      {View ? <View game={game} role={role}/> : null}
    </PageLayout>
  );
};

export default observer(GameResult);
